// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

// doc-exempt: a copy of AYNI 3D's src/shared/coop-game/types.ts; never edit it here (scripts/sync-ayni3d-copy.ts)
/**
 * Types for the cooperative kiosk game "Ayni: Team Earth"
 * (docs/KIOSK_COOP_GAME_DESIGN.md, on votes since docs/KIOSK_GAMES_DESIGN.md §4).
 *
 * One to four kiosks play as one team. Each kiosk has a lens, one of the
 * four Earth systems: its slice shows that system and its screen opens on
 * that system's cards. Every decade the team shares one pool of tokens, and
 * the cards are chosen by the votes of the kiosks present. The whole game
 * state is plain JSON so the engine in main can hold it, log it and send
 * views of it.
 */

import type { LocalizedString } from '../dsa-types';
import type { GameSeatBase } from '../games/presence';
import type { CoopReadings } from './readings';

export const COOP_ROLES = ['air', 'climate', 'water', 'fire'] as const;
/** One of the four Earth systems (named roles when each kiosk owned one). */
export type CoopRole = typeof COOP_ROLES[number];

/** The three health meters (0-100, higher is better). Warming is kept in °C. */
export const COOP_METERS = ['air', 'water', 'fire'] as const;
export type CoopMeter = typeof COOP_METERS[number];

export interface CoopPlanet {
  air: number;
  water: number;
  fire: number;
  /** Warming above pre-industrial, in °C. */
  warmingC: number;
}

/** A change to the planet; meters in points, warming in °C. */
export interface CoopEffect {
  air?: number;
  water?: number;
  fire?: number;
  warmingC?: number;
}

export interface CoopRoleInfo {
  role: CoopRole;
  title: LocalizedString;
  meterLabel: LocalizedString;
  /** The live value-encoded dataset shown on this system's slice. */
  datasetId: string;
}

export interface CoopCard {
  id: string;
  /** The system the card belongs to (its tab on the kiosk). */
  role: CoopRole;
  /** Tokens the card costs the team pool. */
  cost: number;
  /** A bridge card also helps this second system. */
  bridge?: CoopRole;
  effect: CoopEffect;
  title: LocalizedString;
  why: LocalizedString;
}

export interface CoopEvent {
  id: string;
  /** The system whose live reading sets how hard this event hits. */
  seededBy: CoopRole;
  effect: CoopEffect;
  /** An extra hit when a meter is already low after the drift. */
  worseWhen?: { meter: CoopMeter; below: number; effect: CoopEffect };
  title: LocalizedString;
  text: LocalizedString;
}

export type CoopPath = 'low' | 'middle' | 'high';

export type CoopPhase = 'lobby' | 'brief' | 'plan' | 'reveal' | 'finale' | 'ended';

export type CoopEndReason = 'finished' | 'stopped' | 'abandoned' | 'empty-lobby';

/** The strongest cause and effect of a round, for the narrator line. */
export type CoopLink = 'dry-land-burns' | 'smoke-fouls-air' | 'heat-dries-land' | 'warming-slowed';

export interface CoopGameSettings {
  rounds: 3 | 5;
  lobbySeconds: number;
  briefSeconds: number;
  /** null = relaxed: the plan phase waits until every present player is ready. */
  planSeconds: number | null;
  revealSeconds: number;
  finaleSeconds: number;
  /** A seat idle this long stops voting until it is touched again. */
  idleSeconds: number;
  /** false = the lobby waits for the operator's start. */
  autoStart: boolean;
}

export interface CoopSeat extends GameSeatBase {
  /** The system this kiosk's slice shows and its screen opens on. */
  lens: CoopRole;
  lobbyReady: boolean;
  /** Tapped "continue" in the current brief or reveal. */
  continued: boolean;
  /** Tapped "ready" in the current plan. */
  ready: boolean;
  /** Card ids this kiosk backs this decade; their costs fit the pool. */
  ballot: string[];
}

export interface CoopRoundReport {
  round: number;
  year: number;
  eventId: string;
  before: CoopPlanet;
  after: CoopPlanet;
  /** Card ids the team played, in the order the vote chose them. */
  played: string[];
  pool: number;
  unspent: number;
  /** Kiosks whose ballots counted. */
  voters: number;
  /** Votes per card id. */
  votes: Record<string, number>;
  /** Played cards every voter backed (counted only with two voters or more). */
  backedByAll: number;
  links: CoopLink[];
}

export interface CoopTeamTotals {
  cardsPlayed: number;
  bridgeCards: number;
  /** Played cards every voter backed, over the rounds with two voters or more. */
  backedByAll: number;
  /** Played cards in the rounds with two voters or more. */
  sharedRoundCards: number;
  /** All votes each card got, over the game. */
  votesByCard: Record<string, number>;
}

export interface CoopGameState {
  phase: CoopPhase;
  endReason: CoopEndReason | null;
  settings: CoopGameSettings;
  seed: number;
  /** Event ids in play order, one per round. */
  eventOrder: string[];
  /** Per-system event strength from today's real data (1 = typical). */
  severity: Record<CoopRole, number>;
  /** Today's readings of each system's live dataset, as far as the renderer has read them. */
  readings: CoopReadings;
  round: number;
  planet: CoopPlanet;
  seats: CoopSeat[];
  /** The team's tokens this round. */
  pool: number;
  /** The plan timer was extended once already (nobody had voted). */
  planExtended: boolean;
  totals: CoopTeamTotals;
  reports: CoopRoundReport[];
  phaseEndsAt: number | null;
}

export type CoopCommand =
  | { type: 'join'; seatId: string; sliceIndex: number; language?: string }
  | { type: 'leave'; seatId: string }
  | { type: 'touch'; seatId: string }
  | { type: 'set_language'; seatId: string; language: string }
  | { type: 'set_lens'; seatId: string; role: CoopRole }
  | { type: 'lobby_ready'; seatId: string }
  | { type: 'continue'; seatId: string }
  | { type: 'vote_card'; seatId: string; cardId: string }
  | { type: 'ready'; seatId: string }
  | { type: 'set_readings'; readings: CoopReadings }
  | { type: 'start' }
  | { type: 'stop' }
  | { type: 'skip_to_finale' };

export type CoopCommandError =
  | 'wrong-phase'
  | 'unknown-seat'
  | 'seats-full'
  | 'slice-taken'
  | 'unknown-card'
  | 'ballot-full'
  | 'no-players';

export interface CoopCommandResult {
  state: CoopGameState;
  error?: CoopCommandError;
}
